import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import {
  Lock,
  Unlock,
  UserPlus,
  DollarSign,
  Heart,
  MessageSquare,
  Share2,
  Repeat2,
  Crown,
  Loader2,
  Check,
  Sparkles,
} from 'lucide-react';

const ACTION_ICONS = {
  follow: UserPlus,
  pay: DollarSign,
  like: Heart,
  comment: MessageSquare,
  share: Share2,
  repost: Repeat2,
  subscribe: Crown,
};

const ACTION_LABELS = {
  follow: 'Follow',
  pay: 'Pay',
  like: 'Like',
  comment: 'Comment',
  share: 'Share',
  repost: 'Repost',
  subscribe: 'Subscribe',
};

export default function LockedOverlay({ post, onUnlocked }) {
  const { token, getAuthHeaders } = useAuth();
  const [lockStatus, setLockStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const lock = post?.content_lock;
  const postId = post?.post_id;

  const fetchStatus = useCallback(async () => {
    if (!postId || !token) { setLoading(false); return; }
    try {
      const res = await fetch(`${API}/content-locks/status/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setLockStatus(data);
        if (data.has_access) {
          setUnlocked(true);
          onUnlocked?.();
        }
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [postId, token]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  // If no lock, public, or already unlocked
  if (!lock || lock.lock_type === 'public') return null;
  if (unlocked || lockStatus?.has_access) return null;

  const handleFollowUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/content-locks/unlock/follow/${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.unlocked) {
        setUnlocked(true);
        toast.success('Content unlocked!');
        onUnlocked?.();
      } else {
        toast.info('Follow added! Checking unlock...');
        await fetchStatus();
      }
    } catch { toast.error('Failed to unlock'); }
    finally { setUnlocking(false); }
  };

  const handlePayUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/content-locks/unlock/pay/${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.already_unlocked) {
        setUnlocked(true);
        onUnlocked?.();
        return;
      }
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Payment setup failed');
      }
    } catch { toast.error('Payment failed'); }
    finally { setUnlocking(false); }
  };

  const handleEngageUnlock = async () => {
    setUnlocking(true);
    try {
      const res = await fetch(`${API}/content-locks/unlock/engage/${postId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (data.unlocked) {
        setUnlocked(true);
        toast.success('Content unlocked!');
        onUnlocked?.();
      } else {
        const missing = Object.entries(data.actions || {})
          .filter(([, met]) => !met)
          .map(([a]) => ACTION_LABELS[a] || a);
        toast.info(`Still need: ${missing.join(', ')}`);
      }
    } catch { toast.error('Failed to check engagement'); }
    finally { setUnlocking(false); }
  };

  const lockType = lock.lock_type;
  const reqs = lockStatus?.requirements || {};

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center" data-testid={`locked-overlay-${postId}`}>
      {/* Blur overlay */}
      <div className="absolute inset-0 bg-background/60 backdrop-blur-xl rounded-xl" />

      {/* Lock content */}
      <div className="relative z-10 text-center px-5 py-6 max-w-xs animate-in fade-in zoom-in-95 duration-300">
        <div className="w-14 h-14 rounded-full bg-muted/50 backdrop-blur-md flex items-center justify-center mx-auto mb-3 border border-border">
          <Lock className="w-6 h-6 text-foreground/80" />
        </div>

        <h3 className="text-base font-bold text-foreground mb-1">Exclusive Content</h3>

        {/* Lock message */}
        {lockType === 'follow' && (
          <p className="text-sm text-foreground/80 mb-4">Follow to unlock this content</p>
        )}
        {lockType === 'pay' && (
          <p className="text-sm text-foreground/80 mb-4">
            Pay <span className="font-bold text-white">${lock.price}</span> to unlock
          </p>
        )}
        {lockType === 'engage' && (
          <p className="text-sm text-foreground/80 mb-4">Engage to unlock this content</p>
        )}
        {lockType === 'subscribe' && (
          <p className="text-sm text-foreground/80 mb-4">Subscribe to access exclusive content</p>
        )}
        {lockType === 'custom' && (
          <p className="text-sm text-foreground/80 mb-4">Complete requirements to unlock</p>
        )}

        {/* Requirements checklist */}
        {Object.keys(reqs).length > 0 && (
          <div className="flex flex-wrap justify-center gap-2 mb-4">
            {Object.entries(reqs).map(([key, val]) => {
              const Icon = ACTION_ICONS[key] || Lock;
              const met = val.met;
              return (
                <Badge key={key} data-testid={`req-${key}`}
                  className={`flex items-center gap-1 text-xs px-2 py-1 ${
                    met
                      ? 'bg-green-500/20 text-green-400 border-green-500/30'
                      : 'bg-muted/50 text-foreground/80 border-border'
                  }`}>
                  {met ? <Check className="w-3 h-3" /> : <Icon className="w-3 h-3" />}
                  {key === 'pay' && val.price ? `$${val.price}` : ACTION_LABELS[key] || key}
                </Badge>
              );
            })}
          </div>
        )}

        {/* Unlock buttons */}
        <div className="space-y-2">
          {(lockType === 'follow' || (lockType === 'custom' && reqs.follow && !reqs.follow.met)) && (
            <Button data-testid="unlock-follow-btn" onClick={handleFollowUnlock} disabled={unlocking}
              className="w-full bg-violet-600 hover:bg-violet-700 text-foreground text-sm rounded-full">
              {unlocking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Follow to Unlock
            </Button>
          )}

          {(lockType === 'pay' || (lockType === 'custom' && reqs.pay && !reqs.pay.met)) && (
            <Button data-testid="unlock-pay-btn" onClick={handlePayUnlock} disabled={unlocking}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-foreground text-sm rounded-full">
              {unlocking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <DollarSign className="w-4 h-4 mr-2" />}
              Pay ${reqs.pay?.price || lock.price || '?'} to Unlock
            </Button>
          )}

          {lockType === 'engage' && (
            <Button data-testid="unlock-engage-btn" onClick={handleEngageUnlock} disabled={unlocking}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-foreground text-sm rounded-full">
              {unlocking ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Check & Unlock
            </Button>
          )}

          {lockType === 'subscribe' && (
            <Button data-testid="unlock-subscribe-btn" disabled className="w-full bg-orange-600 text-foreground text-sm rounded-full opacity-70">
              <Crown className="w-4 h-4 mr-2" />
              Subscribe (Coming Soon)
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
